import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic,
  Tooltip,
  Badge,
  Tabs,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  TeamOutlined,
  UserOutlined,
  CopyOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  RobotOutlined,
  DesktopOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groupApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Group {
  id: number;
  name: string;
  description: string;
  telegram_group_id: string;
  invite_link: string;
  member_count: number;
  max_members: number;
  is_active: boolean;
  created_at: string;
  approval_status?: string;
  submitted_at?: string;
  approved_at?: string;
  bot_name?: string;
  user_telegram_id?: number;
  user_first_name?: string;
  chat_type?: string;
}

// Telegram群组类型对应的真实成员上限
const getTelegramMaxMembers = (chatType?: string): number => {
  switch (chatType) {
    case 'supergroup': return 200000;
    case 'channel': return 200000;
    case 'group': return 200;
    default: return 200000; // 默认按超级群组
  }
};

interface ApprovalLog {
  id: number;
  group_id: number | null;
  telegram_group_id: number;
  group_name: string;
  action: string;
  source: string;
  operator_id: number | null;
  operator_telegram_id: number | null;
  operator_name: string | null;
  merchant_id: number | null;
  merchant_telegram_id: number | null;
  merchant_name: string | null;
  reason: string | null;
  created_at: string;
}

const GroupManagement: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isInviteLinkModalVisible, setIsInviteLinkModalVisible] = useState(false);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [rejectingGroup, setRejectingGroup] = useState<Group | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState('all');
  const [pendingPage, setPendingPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(10);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(10);
  const [logsActionFilter, setLogsActionFilter] = useState<string | undefined>(undefined);
  const [logsSourceFilter, setLogsSourceFilter] = useState<string | undefined>(undefined);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // 获取群组列表
  const { data: groupsData, isLoading, refetch } = useQuery(
    ['groups', searchText, page, pageSize, sortOrder],
    () =>
      groupApi.getGroups({
        page,
        pageSize,
        search: searchText,
        sort_by: 'created_at',
        sort_order: sortOrder,
      }),
    {
      keepPreviousData: true,
    }
  );

  // 获取待审批群组列表
  const { data: pendingGroupsData, isLoading: isPendingLoading, refetch: refetchPending } = useQuery(
    ['pending-groups', pendingPage, pendingPageSize],
    () =>
      groupApi.getPendingGroups({
        page: pendingPage,
        pageSize: pendingPageSize,
      }),
    {
      keepPreviousData: true,
    }
  );

  // 获取审批记录列表
  const { data: approvalLogsData, isLoading: isLogsLoading, refetch: refetchLogs } = useQuery(
    ['approval-logs', logsPage, logsPageSize, logsActionFilter, logsSourceFilter],
    () =>
      groupApi.getApprovalLogs({
        page: logsPage,
        pageSize: logsPageSize,
        action: logsActionFilter,
        source: logsSourceFilter,
      }),
    {
      keepPreviousData: true,
    }
  );

  // 获取审批统计
  const { data: approvalStatsData } = useQuery(
    ['approval-logs-stats'],
    () => groupApi.getApprovalLogsStats(),
    {
      staleTime: 30000,
    }
  );

  // 自动审批配置
  const { data: autoApproveData } = useQuery(
    ['auto-approve'],
    () => groupApi.getAutoApprove(),
    { staleTime: 10000 }
  );
  const autoApproveEnabled = (autoApproveData as any)?.enabled ?? false;

  const autoApproveMutation = useMutation(
    (enabled: boolean) => groupApi.setAutoApprove(enabled),
    {
      onSuccess: (_data: any, enabled: boolean) => {
        message.success(enabled ? '自动审批已开启' : '自动审批已关闭');
        queryClient.invalidateQueries({ queryKey: ['auto-approve'] });
      },
      onError: () => {
        message.error('设置失败');
      },
    }
  );

  // 创建群组
  const createMutation = useMutation(groupApi.createGroup, {
    onSuccess: () => {
      message.success('群组创建成功');
      setIsModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: () => {
      message.error('群组创建失败');
    },
  });

  // 更新群组
  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => groupApi.updateGroup(id, data),
    {
      onSuccess: () => {
        message.success('群组更新成功');
        setIsModalVisible(false);
        form.resetFields();
        setEditingGroup(null);
        queryClient.invalidateQueries({ queryKey: ['groups'] });
      },
      onError: () => {
        message.error('群组更新失败');
      },
    }
  );

  // 删除群组
  const deleteMutation = useMutation(groupApi.deleteGroup, {
    onSuccess: () => {
      message.success('群组删除成功');
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: () => {
      message.error('群组删除失败');
    },
  });

  // 生成邀请链接
  const generateInviteMutation = useMutation(groupApi.generateInviteLink, {
    onSuccess: (data: any) => {
      message.success('新邀请链接已生成');
      if (selectedGroup) {
        setSelectedGroup({
          ...selectedGroup,
          invite_link: data.invite_link,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: () => {
      // 全局拦截器已展示具体错误信息
    },
  });

  // 批准群组
  const approveMutation = useMutation(groupApi.approveGroup, {
    onSuccess: () => {
      message.success('群组已批准');
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['pending-groups'] });
      queryClient.invalidateQueries({ queryKey: ['approval-logs'] });
      queryClient.invalidateQueries({ queryKey: ['approval-logs-stats'] });
    },
    onError: () => {
      message.error('批准失败');
    },
  });

  // 拒绝群组
  const rejectMutation = useMutation(
    ({ id, reason }: { id: number; reason?: string }) => groupApi.rejectGroup(id, reason),
    {
      onSuccess: () => {
        message.success('群组已拒绝');
        setIsRejectModalVisible(false);
        setRejectingGroup(null);
        setRejectReason('');
        queryClient.invalidateQueries({ queryKey: ['groups'] });
        queryClient.invalidateQueries({ queryKey: ['pending-groups'] });
        queryClient.invalidateQueries({ queryKey: ['approval-logs'] });
        queryClient.invalidateQueries({ queryKey: ['approval-logs-stats'] });
      },
      onError: () => {
        message.error('拒绝失败');
      },
    }
  );

  const groups = (groupsData as any)?.items || [];
  const total = (groupsData as any)?.total || 0;
  const pendingGroups = (pendingGroupsData as any)?.items || [];
  const pendingTotal = (pendingGroupsData as any)?.total || 0;
  const approvalLogs = (approvalLogsData as any)?.items || [];
  const logsTotal = (approvalLogsData as any)?.total || 0;
  const approvalStats = (approvalStatsData as any) || { by_action: {}, by_source: {}, total: 0 };

  const handleAdd = () => {
    setEditingGroup(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (group: Group) => {
    setEditingGroup(group);
    form.setFieldsValue({
      name: group.name,
      description: group.description,
      telegram_group_id: group.telegram_group_id,
      max_members: group.max_members,
      is_active: group.is_active,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleGenerateInviteLink = (group: Group) => {
    setSelectedGroup(group);
    setIsInviteLinkModalVisible(true);
  };

  const handleCopyLink = (link: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(link).then(() => {
        message.success('邀请链接已复制到剪贴板');
      }).catch(() => {
        fallbackCopy(link);
      });
    } else {
      fallbackCopy(link);
    }
  };

  const fallbackCopy = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success('邀请链接已复制到剪贴板');
    } catch {
      message.error('复制失败，请手动复制');
    }
    document.body.removeChild(textarea);
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingGroup) {
        updateMutation.mutate({
          id: editingGroup.id,
          data: values,
        });
      } else {
        createMutation.mutate(values);
      }
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingGroup(null);
  };

  const handleGenerateNewLink = () => {
    if (selectedGroup) {
      generateInviteMutation.mutate(selectedGroup.id);
    }
  };

  const handleApprove = (group: Group) => {
    approveMutation.mutate(group.id);
  };

  const handleReject = (group: Group) => {
    setRejectingGroup(group);
    setRejectReason('');
    setIsRejectModalVisible(true);
  };

  const handleRejectConfirm = () => {
    if (rejectingGroup) {
      rejectMutation.mutate({
        id: rejectingGroup.id,
        reason: rejectReason || undefined,
      });
    }
  };

  const getApprovalStatusTag = (status: string | undefined) => {
    switch (status) {
      case 'approved':
        return <Tag color="success" icon={<CheckCircleOutlined />}>已批准</Tag>;
      case 'pending':
        return <Tag color="warning" icon={<ClockCircleOutlined />}>待审批</Tag>;
      case 'rejected':
        return <Tag color="error" icon={<CloseCircleOutlined />}>已拒绝</Tag>;
      default:
        return <Tag color="default">未知</Tag>;
    }
  };

  const getActionTag = (action: string) => {
    switch (action) {
      case 'approved':
        return <Tag color="success" icon={<CheckCircleOutlined />}>批准</Tag>;
      case 'rejected':
        return <Tag color="error" icon={<CloseCircleOutlined />}>拒绝</Tag>;
      case 'submitted':
        return <Tag color="processing" icon={<ClockCircleOutlined />}>提交</Tag>;
      default:
        return <Tag color="default">{action}</Tag>;
    }
  };

  const getSourceTag = (source: string) => {
    switch (source) {
      case 'bot':
        return <Tag color="blue" icon={<RobotOutlined />}>机器人</Tag>;
      case 'web':
        return <Tag color="purple" icon={<DesktopOutlined />}>后台管理</Tag>;
      case 'auto':
        return <Tag color="green" icon={<RobotOutlined />}>自动审批</Tag>;
      default:
        return <Tag color="default">{source}</Tag>;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '群组名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Group) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">ID: {record.telegram_group_id}</div>
        </div>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '成员数量',
      key: 'members',
      render: (record: Group) => {
        const realMax = getTelegramMaxMembers(record.chat_type);
        const displayMax = realMax >= 200000 ? '20万' : String(realMax);
        return (
          <div>
            <div className="flex items-center space-x-2">
              <Badge
                count={record.member_count}
                overflowCount={999999}
                style={{ backgroundColor: '#52c41a' }}
              />
              <span className="text-gray-500">/ {displayMax}</span>
            </div>
            <div className="w-20 bg-gray-200 rounded-full h-1 mt-1">
              <div
                className="bg-blue-500 h-1 rounded-full"
                style={{
                  width: `${Math.min((record.member_count / realMax) * 100, 100)}%`,
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '审批状态',
      dataIndex: 'approval_status',
      key: 'approval_status',
      render: (status: string) => getApprovalStatusTag(status),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (date ? formatBeijingTime(date) : '-'),
    },
    {
      title: '操作',
      key: 'action',
      render: (record: Group) => (
        <Space>
          <Tooltip title="生成邀请链接">
            <Button
              type="text"
              icon={<LinkOutlined />}
              size="small"
              onClick={() => handleGenerateInviteLink(record)}
            />
          </Tooltip>
          <Tooltip title="编辑群组">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定彻底删除这个群组吗？"
            description="此操作将删除群组记录并让机器人退出该群组"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除群组">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                size="small"
                danger
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 待审批群组表格列
  const pendingColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '群组名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Group) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">ID: {record.telegram_group_id}</div>
        </div>
      ),
    },
    {
      title: '关联机器人',
      dataIndex: 'bot_name',
      key: 'bot_name',
      render: (text: string) => text || '-',
    },
    {
      title: '商户信息',
      key: 'user_info',
      render: (record: Group) => (
        <div>
          <div className="font-medium">{record.user_first_name || '-'}</div>
          {record.user_telegram_id && (
            <div className="text-xs text-gray-500">ID: {record.user_telegram_id}</div>
          )}
        </div>
      ),
    },
    {
      title: '成员数量',
      dataIndex: 'member_count',
      key: 'member_count',
      render: (count: number) => count || 0,
    },
    {
      title: '提交时间',
      dataIndex: 'submitted_at',
      key: 'submitted_at',
      render: (date: string) => (date ? formatBeijingTime(date) : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (record: Group) => (
        <Space>
          <Popconfirm
            title="确定批准这个群组吗？"
            description="批准后机器人将在该群组正常工作"
            onConfirm={() => handleApprove(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              size="small"
              loading={approveMutation.isLoading}
            >
              批准
            </Button>
          </Popconfirm>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            size="small"
            onClick={() => handleReject(record)}
          >
            拒绝
          </Button>
        </Space>
      ),
    },
  ];

  // 审批记录表格列
  const logsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '群组名称',
      dataIndex: 'group_name',
      key: 'group_name',
      render: (text: string, record: ApprovalLog) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">TG ID: {record.telegram_group_id}</div>
        </div>
      ),
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string) => getActionTag(action),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source: string) => getSourceTag(source),
    },
    {
      title: '操作人',
      key: 'operator',
      render: (record: ApprovalLog) => (
        <div>
          <div className="font-medium">{record.operator_name || '-'}</div>
          {record.operator_telegram_id && (
            <div className="text-xs text-gray-500">ID: {record.operator_telegram_id}</div>
          )}
        </div>
      ),
    },
    {
      title: '商户',
      key: 'merchant',
      render: (record: ApprovalLog) => (
        <div>
          <div className="font-medium">{record.merchant_name || '-'}</div>
          {record.merchant_telegram_id && (
            <div className="text-xs text-gray-500">ID: {record.merchant_telegram_id}</div>
          )}
        </div>
      ),
    },
    {
      title: '原因/备注',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason: string | null) => reason || '-',
    },
    {
      title: '操作时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => (date ? formatBeijingTime(date) : '-'),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 页面标题和统计 */}
      <div>
        <Title level={2} className="mb-4">
          群组管理
        </Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="总群组数"
                value={total}
                prefix={<TeamOutlined className="text-blue-500" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="活跃群组"
                value={groups.filter((g: Group) => g.is_active).length}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="待审批"
                value={pendingTotal}
                valueStyle={{ color: pendingTotal > 0 ? '#faad14' : undefined }}
                prefix={<ClockCircleOutlined className="text-yellow-500" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="总成员数"
                value={groups.reduce((sum: number, g: Group) => sum + g.member_count, 0)}
                prefix={<UserOutlined className="text-orange-500" />}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* 操作栏和表格 */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'all',
              label: '全部群组',
              children: (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <Space>
                      <Input.Search
                        placeholder="搜索群组名称"
                        allowClear
                        style={{ width: 300 }}
                        onSearch={setSearchText}
                      />
                      <Select
                        value={sortOrder}
                        style={{ width: 160 }}
                        onChange={(value) => {
                          setSortOrder(value);
                          setPage(1);
                        }}
                        options={[
                          { value: 'desc', label: '创建时间：新 → 旧' },
                          { value: 'asc', label: '创建时间：旧 → 新' },
                        ]}
                      />
                      <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                        刷新
                      </Button>
                      <Tooltip title="开启后新群组将自动审批通过，无需手动操作">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          自动审批
                          <Switch
                            checked={autoApproveEnabled}
                            onChange={(checked) => autoApproveMutation.mutate(checked)}
                            loading={autoApproveMutation.isLoading}
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    </Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                      添加群组
                    </Button>
                  </div>
                  <Table
                    columns={columns}
                    dataSource={groups}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                      current: page,
                      pageSize: pageSize,
                      total: total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `共 ${total} 条记录`,
                      onChange: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                      },
                    }}
                  />
                </>
              ),
            },
            {
              key: 'pending',
              label: (
                <Badge count={pendingTotal} offset={[10, 0]} size="small">
                  待审批
                </Badge>
              ),
              children: (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-gray-500">
                      以下群组等待审批，批准后机器人将正常响应
                    </div>
                    <Button icon={<ReloadOutlined />} onClick={() => refetchPending()}>
                      刷新
                    </Button>
                  </div>
                  <Table
                    columns={pendingColumns}
                    dataSource={pendingGroups}
                    rowKey="id"
                    loading={isPendingLoading}
                    pagination={{
                      current: pendingPage,
                      pageSize: pendingPageSize,
                      total: pendingTotal,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `共 ${total} 条记录`,
                      onChange: (p, ps) => {
                        setPendingPage(p);
                        setPendingPageSize(ps);
                      },
                    }}
                    locale={{
                      emptyText: (
                        <div className="py-8 text-gray-400">
                          <ClockCircleOutlined style={{ fontSize: 48 }} />
                          <p className="mt-4">暂无待审批的群组</p>
                        </div>
                      ),
                    }}
                  />
                </>
              ),
            },
            {
              key: 'logs',
              label: (
                <span>
                  <HistoryOutlined /> 审批记录
                </span>
              ),
              children: (
                <>
                  {/* 审批统计卡片 */}
                  <Row gutter={[16, 16]} className="mb-4">
                    <Col xs={24} sm={8}>
                      <Card size="small">
                        <Statistic
                          title="总审批次数"
                          value={approvalStats.total || 0}
                          prefix={<HistoryOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Card size="small">
                        <Statistic
                          title="批准次数"
                          value={approvalStats.by_action?.approved || 0}
                          valueStyle={{ color: '#52c41a' }}
                          prefix={<CheckCircleOutlined />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Card size="small">
                        <Statistic
                          title="拒绝次数"
                          value={approvalStats.by_action?.rejected || 0}
                          valueStyle={{ color: '#ff4d4f' }}
                          prefix={<CloseCircleOutlined />}
                        />
                      </Card>
                    </Col>
                  </Row>

                  {/* 筛选和表格 */}
                  <div className="flex justify-between items-center mb-4">
                    <Space>
                      <Select
                        placeholder="筛选操作类型"
                        allowClear
                        style={{ width: 140 }}
                        value={logsActionFilter}
                        onChange={setLogsActionFilter}
                      >
                        <Option value="approved">批准</Option>
                        <Option value="rejected">拒绝</Option>
                        <Option value="submitted">提交</Option>
                      </Select>
                      <Select
                        placeholder="筛选来源"
                        allowClear
                        style={{ width: 140 }}
                        value={logsSourceFilter}
                        onChange={setLogsSourceFilter}
                      >
                        <Option value="bot">机器人</Option>
                        <Option value="web">后台管理</Option>
                        <Option value="auto">自动审批</Option>
                      </Select>
                    </Space>
                    <Button icon={<ReloadOutlined />} onClick={() => refetchLogs()}>
                      刷新
                    </Button>
                  </div>
                  <Table
                    columns={logsColumns}
                    dataSource={approvalLogs}
                    rowKey="id"
                    loading={isLogsLoading}
                    pagination={{
                      current: logsPage,
                      pageSize: logsPageSize,
                      total: logsTotal,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `共 ${total} 条记录`,
                      onChange: (p, ps) => {
                        setLogsPage(p);
                        setLogsPageSize(ps);
                      },
                    }}
                    locale={{
                      emptyText: (
                        <div className="py-8 text-gray-400">
                          <HistoryOutlined style={{ fontSize: 48 }} />
                          <p className="mt-4">暂无审批记录</p>
                        </div>
                      ),
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* 添加/编辑群组弹窗 */}
      <Modal
        title={editingGroup ? '编辑群组' : '添加群组'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        confirmLoading={createMutation.isLoading || updateMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            is_active: true,
            max_members: 1000,
          }}
        >
          <Form.Item
            name="name"
            label="群组名称"
            rules={[{ required: true, message: '请输入群组名称' }]}
          >
            <Input placeholder="请输入群组名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="群组描述"
            rules={[{ required: true, message: '请输入群组描述' }]}
          >
            <TextArea rows={3} placeholder="请输入群组描述" />
          </Form.Item>

          <Form.Item
            name="telegram_group_id"
            label="Telegram群组ID"
            rules={[{ required: true, message: '请输入Telegram群组ID' }]}
          >
            <Input placeholder="例如: -1001234567890" />
          </Form.Item>

          <Form.Item
            name="max_members"
            label="最大成员数"
            rules={[{ required: true, message: '请输入最大成员数' }]}
          >
            <Input type="number" placeholder="请输入最大成员数" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value={true}>启用</Option>
              <Option value={false}>禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 邀请链接弹窗 */}
      <Modal
        title="群组邀请链接"
        open={isInviteLinkModalVisible}
        onCancel={() => setIsInviteLinkModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsInviteLinkModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={500}
      >
        {selectedGroup && (
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-600 mb-2">群组名称</div>
              <div className="font-medium">{selectedGroup.name}</div>
            </div>

            <div>
              <div className="text-sm text-gray-600 mb-2">当前邀请链接</div>
              <div className="flex items-center space-x-2">
                <Input
                  value={selectedGroup.invite_link || '暂无邀请链接'}
                  readOnly
                  className="flex-1"
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyLink(selectedGroup.invite_link)}
                  disabled={!selectedGroup.invite_link}
                >
                  复制
                </Button>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="text-sm text-yellow-800">
                <strong>注意：</strong>
                <ul className="mt-2 ml-4 list-disc">
                  <li>此链接具有审核功能，只有通过验证的用户才能加入</li>
                  <li>链接泄露不会影响群组安全</li>
                  <li>可以随时生成新的邀请链接</li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <Button
                type="primary"
                onClick={handleGenerateNewLink}
                loading={generateInviteMutation.isLoading}
              >
                生成新链接
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 拒绝原因弹窗 */}
      <Modal
        title="拒绝群组"
        open={isRejectModalVisible}
        onOk={handleRejectConfirm}
        onCancel={() => {
          setIsRejectModalVisible(false);
          setRejectingGroup(null);
          setRejectReason('');
        }}
        okText="确认拒绝"
        cancelText="取消"
        okButtonProps={{ danger: true, loading: rejectMutation.isLoading }}
      >
        {rejectingGroup && (
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-600 mb-1">群组名称</div>
              <div className="font-medium">{rejectingGroup.name}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Telegram ID</div>
              <div>{rejectingGroup.telegram_group_id}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-2">拒绝原因（可选）</div>
              <TextArea
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="请输入拒绝原因，将通知给商户"
              />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-sm text-red-800">
                <strong>注意：</strong>
                <ul className="mt-2 ml-4 list-disc">
                  <li>拒绝后群组记录将被删除</li>
                  <li>机器人将自动退出该群组</li>
                  <li>商户将收到拒绝通知</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GroupManagement;
